import React, { useState, useEffect } from 'react';
import {
  IonButton,
  IonIcon,
  IonSpinner,
  IonCard,
  IonCardContent,
  IonSelect,
  IonSelectOption,
  IonItem,
  IonLabel,
} from '@ionic/react';
import {
  calendarOutline,
  addCircleOutline,
} from 'ionicons/icons';
import { format, isBefore } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Event } from '../types';
import { eventService } from '../services/api';

interface EnhancedEventSelectorProps {
  currentEvent: Event | null;
  onEventSelect: (event: Event | null) => void;
  onCreateEvent?: () => void;
  showAllEvents?: boolean;
}

const EnhancedEventSelector: React.FC<EnhancedEventSelectorProps> = ({
  currentEvent,
  onEventSelect,
  onCreateEvent,
  showAllEvents = false,
}) => {
  const { t } = useTranslation();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const response = await eventService.getAllEvents();
      const allEvents = Array.isArray(response) ? response : [];

      // Sort events by date (upcoming first)
      const sortedEvents = allEvents.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateA.getTime() - dateB.getTime();
      });

      setEvents(sortedEvents);
    } catch (error) {
      console.error('Failed to load events:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const categorizeEvents = () => {
    const now = new Date();
    const upcoming: Event[] = [];
    const past: Event[] = [];

    events.forEach((event) => {
      const eventDate = new Date(event.date);
      if (event.status === 'active' && !isBefore(eventDate, now)) {
        upcoming.push(event);
      } else {
        past.push(event);
      }
    });

    return { upcoming, past };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <IonSpinner />
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <IonCard className="m-0 shadow-md">
        <IonCardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <IonIcon
                icon={calendarOutline}
                className="text-2xl text-gray-400"
              />
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('events.noEvents')}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  {t('events.createEventToStart')}
                </p>
              </div>
            </div>
            {onCreateEvent && (
              <IonButton
                fill="clear"
                size="small"
                onClick={onCreateEvent}
                className="m-0"
              >
                <IonIcon icon={addCircleOutline} className="text-2xl" />
              </IonButton>
            )}
          </div>
        </IonCardContent>
      </IonCard>
    );
  }

  const { upcoming, past } = categorizeEvents();
  const eventsToShow = showAllEvents ? [...upcoming, ...past] : upcoming;

  return (
    <IonCard className="m-0 shadow-md">
      <IonCardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 flex-1">
            <IonIcon
              icon={calendarOutline}
              className="text-2xl text-primary-600 dark:text-primary-400 flex-shrink-0"
            />

            <div className="flex-1">
              <IonItem lines="none" className="p-0">
                <IonSelect
                  value={currentEvent?._id}
                  onIonChange={(e) => {
                    const selectedEvent = events.find(
                      (ev) => ev._id === e.detail.value
                    );
                    onEventSelect(selectedEvent || null);
                  }}
                  interface="action-sheet"
                  placeholder={t('events.selectEvent')}
                  className="w-full event-selector-dropdown"
                >
                  {upcoming.length > 0 && (
                    <>
                      <IonSelectOption value="" disabled>
                        ── {t('events.upcomingEvents')} ──
                      </IonSelectOption>
                      {upcoming.map((event) => (
                        <IonSelectOption key={event._id} value={event._id} className="select-text">
                          {event.name} - {format(new Date(event.date), 'MMM dd')} - {event.venue}
                        </IonSelectOption>
                      ))}
                    </>
                  )}
                  
                  {showAllEvents && past.length > 0 && (
                    <>
                      <IonSelectOption value="" disabled>
                        ── {t('events.pastEvents')} ──
                      </IonSelectOption>
                      {past.map((event) => (
                        <IonSelectOption key={event._id} value={event._id} className="select-text">
                          {event.name} - {format(new Date(event.date), 'MMM dd')} - {event.venue}
                        </IonSelectOption>
                      ))}
                    </>
                  )}
                </IonSelect>
              </IonItem>
              
              {currentEvent && (
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  <span>{format(new Date(currentEvent.date), 'MMMM dd, yyyy')}</span>
                  <span className="mx-2">•</span>
                  <span>{currentEvent.venue}</span>
                </div>
              )}
            </div>
          </div>

          {onCreateEvent && (
            <IonButton
              fill="clear"
              size="default"
              onClick={onCreateEvent}
              className="m-0 ml-2 --padding-end:8px --padding-start:8px"
            >
              <IonIcon icon={addCircleOutline} className="text-2xl" />
            </IonButton>
          )}
        </div>
      </IonCardContent>
    </IonCard>
  );
};

export default EnhancedEventSelector;